import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewContainerRef,
} from '@angular/core'
import _ from 'lodash'
import { TooltipComponent } from '../tooltip/tooltip.component'
import { fromEvent, Subscription } from 'rxjs'

@Component({
    selector: 'ca-absolute-tooltip',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <span
            (mouseenter)="showTooltip($event)"
            (mouseleave)="hideTooltip()"
            (click)="hideTooltip()"
            class="component-absoluteTooltip {{addedClasses}}">
            <ng-content></ng-content>
        </span>`,
    standalone: false
})
export class AbsoluteTooltipComponent implements OnInit, OnChanges, OnDestroy {

    @Input() position: string
    @Input() type: string
    @Input() content: string
    @Input() classes: string
    @Input() header: string
    @Input() asideClasses: string
    addedClasses: string
    innerWidth: number
    tooltipComponent: ComponentRef<TooltipComponent>

    windowResizeHandler: Subscription

    constructor(
        @Inject(ViewContainerRef) private viewContainerRef: ViewContainerRef,
        @Inject(ElementRef) private elementRef: ElementRef,
        @Inject(ChangeDetectorRef) private changeDetector,
    ) {
        this.windowResizeHandler = fromEvent(window, 'resize').subscribe(
            () => this.innerWidth = window.innerWidth
        )
        this.changeDetector.markForCheck()
    }

    ngOnInit() {
        this.setClasses()
        this.innerWidth = window.innerWidth
    }

    ngOnChanges() {
        this.setClasses()
    }

    ngOnDestroy() {
        this.windowResizeHandler.unsubscribe()
    }

    showTooltip(event: MouseEvent): void {
        this.tooltipComponent = this.viewContainerRef.createComponent(TooltipComponent)
        document.body.appendChild(this.tooltipComponent.location.nativeElement)
        this.tooltipComponent.setInput('asideClass', this.asideClasses)
        this.tooltipComponent.setInput('position', this.position)
        this.tooltipComponent.setInput('header', this.header)
        this.tooltipComponent.setInput('jsTooltip', true)
        this.tooltipComponent.setInput('content', this.content ? this.content : this.getAsideContent())

        let left = this.getLeftPosition(event)
        let top = this.getTopPosition(event)
        let maxWidth = 0
        if (this.position.indexOf('left') > -1) {
            maxWidth = Math.max(100, this.innerWidth - left - 20)
        }
        this.tooltipComponent.setInput('left', left + 'px')
        this.tooltipComponent.setInput('top', top + 'px')
        this.tooltipComponent.setInput('width', maxWidth + 'px')
    }

    hideTooltip(): void {
        this.tooltipComponent?.destroy()
    }

    private setClasses(): void {
        this.addedClasses = this.getClassFromType(this.type)
        if (this.classes) {
            this.addedClasses += ' ' + this.classes
        }
    }

    private getAsideContent(): string {
        const asideElem: Element = this.elementRef.nativeElement.querySelector('aside')

        return asideElem?.innerHTML?.replace(/ng-\w+="[^"]+"/gm, '')
    }

    private getLeftPosition(event: MouseEvent): number {
        const target = <Element>event.target

        let targetLeft = target.getBoundingClientRect().left
        let targetRight = target.getBoundingClientRect().right
        let left = 0

        // initial container position
        switch (this.position) {
            case 'top-center':
            case 'bottom-center':
                left += targetLeft + (targetRight - targetLeft) / 2
                break
            case 'top-right':
            case 'bottom-right':
                left += targetRight
                break

            default: // top, top-left, bottom, bottom-left, unspecified
                left += targetLeft
                break
        }

        // icon type correction
        if (this.type && _.startsWith(this.type, 'icon')) {
            if (this.position.indexOf('center') > -1) {
                left -= 4
            } else if (this.position.indexOf('right') > -1) {
                left += 14
            } else {
                left -= 23
            }
        }

        return left
    }

    private getTopPosition(event: MouseEvent): number {
        let top = (<Element>event.target).getBoundingClientRect().top + window.scrollY

        if (this.type && (_.startsWith(this.type, 'icon') || this.type === 'text-plain')) {
            if (this.position.indexOf('top') > -1) {
                top -= 8
            } else if (this.position.indexOf('bottom') > -1) {
                top += 8
            }
        }

        if (this.type && this.type === 'text-h1') {
            if (this.position.indexOf('top') > -1) {
                top -= 15
            } else if (this.position.indexOf('bottom') > -1) {
                top += 15
            }
        }

        return top
    }

    private getClassFromType(type: string): string {
        switch (type) {
            case 'icon-warning':
                return 'fa fa-warning'
            case 'icon-warning-inactive':
                return 'fa fa-warning fa-gray'
            case 'icon-warning-checkable':
                return 'fa fa-warning fa-check-after'
            case 'icon-info':
                return 'fa fa-info-circle'
            default:
                return ''
        }
    }

}
